<template>
  <tr :key="item.id" :data-cy="`device${item.deviceSerial}Row`">
    <td class="mobile-list" :class="{ 'ml-6': isChild }">
      <div class="item-title">
        <ActionMenu
          :deviceId="item.id"
          :accessLevel="item.accessCode"
          class="ml-n1"
          @openDialog="openDialog"
        />
        <v-tooltip location="bottom" :max-width="600" :disabled="!isNameOverflowing">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="device-name" :ref="item.id">
              <span v-if="item.deviceName">{{ item.deviceName }}</span>
              <span v-else>{{ $t('manageDevices.unnamedDevice') }}</span>
            </div>
          </template>
          <span>{{ item.deviceName }}</span>
        </v-tooltip>
        <v-spacer></v-spacer>
        <TableCondition
          v-if="canAccess(access.FULL, item.accessCode) && item.showCondition"
          :deviceId="item.id"
          :accessLevel="item.accessCode"
        />
      </div>
      <ul class="flex-content" :class="{ expandable: itemCanBeExpanded }">
        <li class="flex-item">
          <span> {{ $t('manageDevices.tableHeaders.deviceLocationName') }}:&nbsp; </span>
          <span>{{ item.deviceLocationName }}</span>
        </li>
        <li class="flex-item">
          <span> {{ $t('manageDevices.tableHeaders.deviceType') }}:&nbsp; </span>
          <span>{{ item.deviceType }}</span>
        </li>
        <li class="flex-item">
          <span> {{ $t('manageDevices.tableHeaders.deviceModel') }}:&nbsp; </span>
          <span>{{ item.deviceModel }}</span>
        </li>
        <li class="flex-item">
          <span> {{ $t('manageDevices.tableHeaders.deviceSerial') }}:&nbsp; </span>
          <span>{{ item.deviceSerial }}</span>
        </li>
        <li class="flex-item">
          <span> {{ $t('manageDevices.tableHeaders.deviceSubscription') }}:&nbsp; </span>
          <span>{{ item.deviceSubscription }}</span>
        </li>
        <li class="flex-item">
          <span>{{ $t('manageDevices.tableHeaders.access') }}:&nbsp;</span>
          <span>{{ item.access }}</span>
        </li>
        <li class="flex-item">
          <span> {{ $t('manageDevices.tableHeaders.lastCommunication') }}:&nbsp; </span>
          <span v-if="item.lastCommunication">{{ item.lastCommunication }}</span>
          <span v-else>{{ $t('manageDevices.lastCommunicationNever') }}</span>
        </li>
        <li class="flex-item">
          <span> {{ measurementLabel }}:&nbsp; </span>
          <span>
            <StyledReading
              v-if="item.showAQI"
              :reading="item.reading"
              :aqiType="aqiType"
              :measurementType="measurementType"
            />
          </span>
        </li>
      </ul>
      <div class="text-end my-2" v-if="itemCanBeExpanded">
        <v-btn
          variant="text"
          class="text-primary flex-row-reverse"
          @click="expand()"
          data-cy="expandCollapseButton"
        >
          {{
            isExpanded
              ? $t('manageDevices.mobileExpansionOptions.hide')
              : $t('manageDevices.mobileExpansionOptions.view')
          }}
        </v-btn>
      </div>
    </td>
  </tr>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import debounce from 'lodash/debounce'
import { deviceModels } from '@/store/subscriptions/utils'
import { measurementTypes } from '@/store/map/utils'
import { NAMESPACES } from '@/store/types'
import { MAP_GETTERS } from '@/store/map'
import { useAccess } from '@/services/device-access'
import { isChildOverflowing } from '@/services/html-utilities'
import ActionMenu from './actionMenu.vue'
import StyledReading from './styledReading.vue'
import TableCondition from './tableCondition.vue'
import type { ManageDeviceRow } from '@/views/manage-devices.vue'

interface DialogData {
  dialog: string
  deviceId: string
}

export default defineComponent({
  name: 'DeviceTableBodyMobile',
  emits: {
    openDialog: (_dialogData: DialogData) => true
  },
  props: {
    item: {
      type: Object as PropType<ManageDeviceRow>,
      required: true
    },
    expand: {
      type: Function as PropType<() => void>,
      default: (): void => undefined
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    isChild: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ActionMenu,
    StyledReading,
    TableCondition
  },
  setup() {
    const { canAccess, access } = useAccess()
    return { canAccess, access }
  },
  computed: {
    aqiType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_AQI_TYPE}`]
    },
    measurementType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
    },
    measurementLabel(): string {
      if (this.measurementType === measurementTypes.pm10) {
        return this.$t('manageDevices.tableHeaders.aqi10')
      } else {
        return this.$t('manageDevices.tableHeaders.aqi25')
      }
    },
    itemCanBeExpanded(): boolean {
      const res =
        this.item.deviceModel === deviceModels.SMART_BRIDGE.toUpperCase() &&
        this.item.associatedDevices?.length > 0
      return res
    }
  },
  data() {
    return {
      isNameOverflowing: false,
      resizeHandler: null as null | (() => void)
    }
  },
  methods: {
    handleResize(): void {
      this.resizeHandler?.()
    },
    openDialog(dialogData: DialogData): void {
      this.$emit('openDialog', dialogData)
    }
  },
  created(): void {
    this.resizeHandler = debounce(() => {
      this.isNameOverflowing = isChildOverflowing(this.$refs[this.item.id + 'Name'] as HTMLElement)
    }, 200)
  }
})
</script>
<style lang="scss" scoped>
.v-data-table td.mobile-list {
  display: flex;
  flex-direction: column;
  height: auto;
  min-height: 48px;
  max-width: 100vw;
  .ma-6 & {
    max-width: calc(100vw - 48px);
  }

  .item-title {
    display: flex;
    line-height: 36px;
    padding-top: 8px;
    .device-name {
      margin-left: 8px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  ul {
    padding-top: 8px;
    padding-bottom: 16px;
    padding-left: 12px;
    list-style-type: none;
    .item-title {
      font-weight: 500;
      font-size: 15px;
    }
  }
  ul.expandable {
    padding-bottom: 0px;
  }
  .mobile-action-menu-container {
    position: absolute;
    top: 16px;
    right: 16px;
  }
  .flex-item {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 4px;
    > span:first-child {
      display: inline-block;
      margin-right: 4px;
      white-space: nowrap;
      color: rgba(0, 0, 0, 0.6);
    }
    > span:last-child {
      display: inline-block;
    }
  }
}
</style>
